import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import {
  triggerRefresh,
  updateNotes,
  updateQualifiers,
  updateSortedSelectedClips,
  updateTimelineSeconds,
  updateVideoPlayedSeconds,
} from '../../../store/videos/videoSlice';
import { toFollowPlayhead } from '../../../store/sharedStore/sharedStoreSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import DeleteTimelineClipModal from '../DeleteTimelineClipModal';
import { TClip, TimelineClip } from '../TimelineClip';
import { CLIP, VIDEOFEED } from '../../../enums';
import styles from './TimelineRow.module.scss';
import { convertSeconds } from '../utils';
import { TTimelineSession } from '..';

export type TRow = {
  clips: TClip[];
  name: string;
  id: string;
  details?: {
    id?: string;
    uuid?: string;
    color?: string;
    category?: string;
  };
};

type TTimelineRow = {
  onClipSelect: (
    clip: TClip,
    topicId: string,
    rowIndex: number,
    clipIndex: number,
    topicName: string,
    doubleClick?: boolean,
    nextMenuOption?: boolean,
  ) => void;
  handleEmptySpaceClick: (e: React.MouseEvent<HTMLDivElement>, rowId: string) => void;
  switchCreateOrSendModal: () => void;
  setClipDrawer: React.Dispatch<React.SetStateAction<{ time_start: number; time_end: number; topicId: string } | null>>;
  clipDrawer: { time_start: number; time_end: number; topicId: string; id?: string; color?: string } | null; // added optional `id` to identify it
  currentFeed?: VIDEOFEED;
  markMargin: number;
  setSession: React.Dispatch<React.SetStateAction<TTimelineSession[]>>;
  topicName: string;
  handleEditClip: (data: CLIP) => void;
  onClipEdgeResize: (event: React.MouseEvent, clip: CLIP, side: 'left' | 'right') => void;
  rowIndex: number;
  topicId: string;
  clips: TClip[];
  emptySelectedRow: () => void;
  zoom: number;
  changedTopicIdsRef: React.MutableRefObject<{
    oldTopicId?: number;
    newTopicId?: number;
  }>;
  canEdit: boolean;
  glowAllClips: boolean;
  glowSelectedClips: boolean;
  suppressQualifiers?: boolean;
  collapsed: boolean;
  dimmedIds: Set<string>;
};

// Helper to clamp overlap so the drawer won't intrude into existing clips
export const findOverlapBoundary = (start: number, end: number, existingClips: TClip[], dragging: 'left' | 'right', drawerId?: string) => {
  for (const c of existingClips) {
    // Skip if it's the same clip (if your drawer has a real ID and you want to exclude itself)
    if (drawerId && c.id === drawerId) continue;

    // If intervals [start, end] and [c.time_start, c.time_end] overlap
    if (start < c.time_end && end > c.time_start) {
      if (dragging === 'left') {
        // Push start to the right edge of the existing clip
        start = Math.max(start, c.time_end);
      } else {
        // Push end to the left edge of the existing clip
        end = Math.min(end, c.time_start);
      }
    }
  }
  return { start, end };
};

const TimelineRowInner: React.FC<TTimelineRow> = ({
  switchCreateOrSendModal,
  handleEmptySpaceClick,
  handleEditClip,
  onClipEdgeResize,
  setClipDrawer,
  onClipSelect,
  currentFeed,
  setSession,
  clipDrawer,
  markMargin,
  topicName,
  rowIndex,
  emptySelectedRow,
  topicId,
  clips,
  zoom,
  canEdit,
  glowAllClips,
  glowSelectedClips,
  suppressQualifiers,
  collapsed,
  dimmedIds,
}) => {
  const videoLength = useAppSelector((state) => state.videoStore.videoLength);
  const sortedSelectedClips = useAppSelector((state) => state.videoStore.sortedSelectedClips);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const clickedClip = useRef(false);
  const dispatch = useAppDispatch();

  const onEmptySpaceClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !clickedClip.current) {
      dispatch(updateSortedSelectedClips([]));
      dispatch(updateNotes([]));
      dispatch(updateQualifiers([]));
      dispatch(triggerRefresh());
      dispatch(toFollowPlayhead(false));
      emptySelectedRow();
      clickedClip.current = false;
      // emptySelectedRow();
      // setClipDrawer(null);
    }
  };

  const rowRef = useRef<HTMLDivElement>(null);

  // const handleSecondaryClick = (e: React.MouseEvent<HTMLDivElement>, clipIndex: number) => {
  //   e.stopPropagation();
  //   e.preventDefault();
  //   if (currentFeed && currentFeed.created_by.id === currentUser?.id) {
  //     dispatch(updateSecondaySelectedClips([{ ...clips[clipIndex], topicId: topicId, clipIndex, rowIndex, topicName }]));
  //     switchDotsMenu(e);
  //   } else if (
  //     currentFeed &&
  //     (currentFeed.highest_user_access?.access.title === ACCESS_TYPE_ENUM.COMMENT_AND_CREATE ||
  //       currentFeed.highest_user_access?.access.title === ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE ||
  //       currentFeed.highest_user_access?.access.title === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE)
  //   ) {
  //     dispatch(updateSecondaySelectedClips([{ ...clips[clipIndex], topicId: topicId, clipIndex, rowIndex, topicName }]));
  //     switchDotsMenu(e);
  //   }
  // };

  const zoomedMargin = useMemo(() => {
    const margin = markMargin + 1;
    return margin / zoom;
  }, [zoom, markMargin]);

  const [dragging, setDragging] = useState<'left' | 'right' | 'center' | null>(null);
  const initialXRef = useRef<number>(0);
  const initialStartRef = useRef<number>(0);
  const initialEndRef = useRef<number>(0);
  const videoLengthInSeconds: number = useMemo(() => {
    return videoLength / 1000;
  }, [videoLength]);

  const handleMouseDown = (e: React.MouseEvent, side: 'left' | 'right') => {
    e.stopPropagation();
    e.preventDefault();
    if (clipDrawer) {
      setDragging(side);
      initialXRef.current = e.clientX;
      initialStartRef.current = clipDrawer.time_start;
      initialEndRef.current = clipDrawer.time_end;
    }
  };

  const leftHandleRef = useRef<HTMLDivElement | null>(null);
  const rightHandleRef = useRef<HTMLDivElement | null>(null);

  const [showSingleHandle, setShowSingleHandle] = useState(false);

  const handleMouseMove = (e: MouseEvent) => {
    if (leftHandleRef.current && rightHandleRef.current) {
      const leftRect = leftHandleRef.current.getBoundingClientRect();
      const rightRect = rightHandleRef.current.getBoundingClientRect();
      if (leftRect.right >= rightRect.left && !showSingleHandle) {
        setShowSingleHandle(true);
      } else if (leftRect.right < rightRect.left && showSingleHandle) {
        setShowSingleHandle(false);
      }
    }

    if (!dragging || !clipDrawer) return;

    const deltaPixels = e.clientX - initialXRef.current;
    const deltaSeconds = deltaPixels / zoomedMargin;

    if (dragging === 'left') {
      let newStart = initialStartRef.current + deltaSeconds;
      // Enforce a minimum 5-second duration
      newStart = Math.min(newStart, clipDrawer.time_end - 1);
      newStart = Math.max(newStart, 0);

      // Temporarily keep the end as-is
      let newEnd = clipDrawer.time_end;

      // Check for overlap and clamp
      const { start } = findOverlapBoundary(newStart, newEnd, clips, 'left', clipDrawer.id);
      newStart = start;

      setClipDrawer({ ...clipDrawer, time_start: newStart });

      dispatch(updateVideoPlayedSeconds(newStart));
      dispatch(updateTimelineSeconds(newStart));
    } else if (dragging === 'right') {
      let newEnd = initialEndRef.current + deltaSeconds;
      // Enforce a minimum 5-second duration
      newEnd = Math.max(newEnd, clipDrawer.time_start + 1);
      newEnd = Math.min(newEnd, videoLengthInSeconds);

      // Temporarily keep the start as-is
      let newStart = clipDrawer.time_start;

      // Check for overlap and clamp
      const { end } = findOverlapBoundary(newStart, newEnd, clips, 'right', clipDrawer.id);
      newEnd = end;

      setClipDrawer({ ...clipDrawer, time_end: newEnd });

      dispatch(updateVideoPlayedSeconds(newEnd));
      dispatch(updateTimelineSeconds(newEnd));
    } else if (dragging === 'center') {
      // Calculate naive shift
      let newStart = initialStartRef.current + deltaSeconds;
      let newEnd = initialEndRef.current + deltaSeconds;

      // 1. Clamp to overall 0 / videoLength
      if (newStart < 0) {
        newEnd -= newStart; // shift the end by same overflow
        newStart = 0;
      }
      if (newEnd > videoLengthInSeconds) {
        const overshoot = newEnd - videoLengthInSeconds;
        newStart -= overshoot;
        newEnd = videoLengthInSeconds;
      }

      // 2. Clamp within the gap between nearest clips
      const sorted = clips
        .filter((c) => c.id !== clipDrawer.id) // exclude self
        .sort((a, b) => a.time_start - b.time_start);

      // find the clip to the left
      const prevClip = [...sorted].reverse().find((c) => c.time_end <= initialStartRef.current);
      // find the clip to the right
      const nextClip = sorted.find((c) => c.time_start >= initialEndRef.current);

      // If there's a previous clip, ensure newStart can't go left of its end
      if (prevClip && newStart < prevClip.time_end) {
        const shift = prevClip.time_end - newStart;
        newStart += shift;
        newEnd += shift;
      }

      // If there's a next clip, ensure newEnd can't go right of its start
      if (nextClip && newEnd > nextClip.time_start) {
        const shift = newEnd - nextClip.time_start;
        newStart -= shift;
        newEnd -= shift;
      }

      setClipDrawer({
        ...clipDrawer,
        time_start: newStart,
        time_end: newEnd,
      });

      dispatch(updateVideoPlayedSeconds(newEnd));
      dispatch(updateTimelineSeconds(newEnd));
    }
  };

  const onDelete = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const handleMouseUp = () => setDragging(null);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, clipDrawer]);

  const clipDrawerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (clipDrawerRef.current && topicId === clipDrawer?.topicId) {
      clipDrawerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }, [clipDrawer?.topicId]); // triggers when a new drawer appears or changes

  const onSendClip = useCallback(() => switchCreateOrSendModal(), [switchCreateOrSendModal]);

  const selectedIdSet = useMemo(() => new Set(sortedSelectedClips.map((c) => c.id)), [sortedSelectedClips]);

  const visibleClips = useMemo(
    () =>
      (clips || []).filter(
        (clip) =>
          clip.time_start < clip.time_end &&
          clip.time_start < videoLength &&
          clip.time_end <= videoLength &&
          clip.time_start > 0 &&
          clip.time_end > 0,
      ),
    [clips, videoLength],
  );

  return (
    <div
      id={`timeline-row-${topicId}`}
      className={cx(styles.timelineRow, collapsed && styles.collapsed)}
      onClick={onEmptySpaceClick}
      onContextMenu={(e) => handleEmptySpaceClick(e, topicId)}
      ref={rowRef}
    >
      {clipDrawer && clipDrawer.topicId === topicId && (
        <div
          ref={clipDrawerRef}
          id={clipDrawer.id}
          className={styles.clipDrawerWrapper}
          data-clip-drawer-handle
          style={{
            left: `${clipDrawer.time_start * zoomedMargin}px`,
            width: `${(clipDrawer.time_end - clipDrawer.time_start) * zoomedMargin}px`,
          }}
        >
          {/* Arrows */}
          <div
            style={{ width: clipDrawer?.time_end - clipDrawer.time_start < 10 ? '3px' : '12px' }}
            className={styles.leftArrow}
            onMouseDown={(e) => handleMouseDown(e, 'left')}
          >
            {clipDrawer?.time_end - clipDrawer.time_start >= 10 && <ChevronLeftIcon style={{ color: 'black' }} />}
          </div>

          <div
            className={styles.centerBar}
            style={{
              background: clipDrawer.color
                ? clipDrawer.color.includes('#')
                  ? clipDrawer.color
                  : `rgb(${clipDrawer?.color.split(',').map((c) => +c * 256)})`
                : 'transparent',
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (clipDrawer) {
                setDragging('center');
                initialXRef.current = e.clientX;
                initialStartRef.current = clipDrawer.time_start;
                initialEndRef.current = clipDrawer.time_end;
              }
            }}
          />

          <div
            style={{
              display: clipDrawer.time_end - clipDrawer.time_start < 10 ? 'block' : 'none',
              left: '-20px',
            }}
            className={styles.smallArrow}
            onMouseDown={(e) => handleMouseDown(e, 'left')}
          >
            <ChevronLeftIcon style={{ color: 'white', height: '15px' }} />
          </div>

          <div
            style={{
              display: clipDrawer.time_end - clipDrawer.time_start < 10 ? 'block' : 'none',
              right: '-20px',
            }}
            className={styles.smallArrow}
            onMouseDown={(e) => handleMouseDown(e, 'right')}
          >
            <ChevronRightIcon style={{ color: 'white', height: '15px' }} />
          </div>

          <div
            style={{ width: clipDrawer?.time_end - clipDrawer.time_start < 10 ? '3px' : '12px' }}
            className={styles.rightArrow}
            onMouseDown={(e) => handleMouseDown(e, 'right')}
          >
            {clipDrawer?.time_end - clipDrawer.time_start >= 10 && <ChevronRightIcon style={{ color: 'black' }} />}
          </div>

          {/* Time bubble(s) */}
          {showSingleHandle && (
            <div className={styles.singleHandle} style={{ right: clipDrawer.time_start < 30 ? '-60px' : '0px' }}>
              {convertSeconds(clipDrawer.time_start, true)} - {convertSeconds(clipDrawer.time_end, true)}
            </div>
          )}
          <div className={styles.clipSizeWrapper}>
            <div className={styles.clipSize}>{convertSeconds(clipDrawer.time_end - clipDrawer.time_start, true)}</div>
          </div>
          <div ref={leftHandleRef} className={styles.leftHandle} style={{ opacity: showSingleHandle ? 0 : 1 }}>
            {convertSeconds(clipDrawer.time_start, true)}
          </div>
          <div ref={rightHandleRef} className={styles.rightHandle} style={{ opacity: showSingleHandle ? 0 : 1 }}>
            {convertSeconds(clipDrawer.time_end, true)}
          </div>
        </div>
      )}
      {currentFeed &&
        visibleClips.length > 0 &&
        visibleClips.map((clip, clipIndex) => {
          return (
            <TimelineClip
              key={`${clip.id}_${clipIndex}`}
              onDelete={onDelete}
              onEdit={() => handleEditClip({ ...clip, topicId, rowIndex, clipIndex, topicName })}
              onEdgeResize={(event, side) => onClipEdgeResize(event, { ...clip, topicId, rowIndex, clipIndex, topicName }, side)}
              onSend={onSendClip}
              currentFeed={currentFeed}
              onClipSelect={onClipSelect}
              clickedClipRef={clickedClip}
              topicId={topicId}
              topicName={topicName}
              rowIndex={rowIndex}
              clipIndex={clipIndex}
              clip={clip}
              dimmed={dimmedIds.has(String(clip.id))}
              collapsed={collapsed}
              zoom={zoom}
              markMargin={markMargin}
              clipDrawerId={clipDrawer?.id}
              canEdit={canEdit}
              isSelected={selectedIdSet.has(clip.id)}
              isGlowing={glowAllClips || (glowSelectedClips && selectedIdSet.has(clip.id))}
              suppressQualifiers={suppressQualifiers}
            />
          );
        })}

      {setSession && <DeleteTimelineClipModal setSession={setSession} open={showDeleteModal} switchModal={onDelete} />}
    </div>
  );
};

export const TimelineRow = React.memo(
  TimelineRowInner,
  (prev, next) =>
    prev.topicId === next.topicId &&
    prev.topicName === next.topicName &&
    prev.rowIndex === next.rowIndex &&
    prev.zoom === next.zoom &&
    prev.markMargin === next.markMargin &&
    prev.canEdit === next.canEdit &&
    prev.currentFeed === next.currentFeed &&
    prev.clips === next.clips &&
    prev.clipDrawer === next.clipDrawer &&
    prev.glowAllClips === next.glowAllClips &&
    prev.glowSelectedClips === next.glowSelectedClips &&
    prev.suppressQualifiers === next.suppressQualifiers &&
    prev.collapsed === next.collapsed &&
    prev.dimmedIds === next.dimmedIds,
) as React.FC<TTimelineRow>;

TimelineRow.displayName = 'TimelineRow';
